import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ClipboardCheck, Save } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import PageHeader from "@/components/PageHeader";
import { Reveal } from "@/components/motion";

const inputCls = "w-full border border-girder bg-steel px-4 py-3 text-sm text-slate-100 placeholder:text-slate-600 focus:border-hivis focus:outline-none";
const labelCls = "mb-1.5 block font-mono text-[11px] uppercase tracking-widest text-slate-500";

export default function DealerProfilePage() {
  const { dealer, checking, setDealer } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);
  const firstRun = dealer && dealer.profile_completed === false;

  useEffect(() => {
    if (checking) return;
    if (!dealer) { navigate("/dealer-portal"); return; }
    api.get("/auth/me")
      .then((r) => setForm({
        name: r.data.name || "", company: r.data.company || "", phone: r.data.phone || "",
        email: r.data.email || "", website: r.data.website || "", shipping_address: r.data.shipping_address || "",
        preferred_contact: r.data.preferred_contact || "Email", notes: r.data.notes || "",
        directory_opt_in: r.data.directory_opt_in !== false,
        directory_show_email: r.data.directory_show_email === true,
        aliases: r.data.email_aliases || [],
      }))
      .catch(() => navigate("/dealer-portal"));
  }, [checking, dealer, navigate]);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const r = await api.put("/auth/profile", form);
      setDealer({ ...dealer, ...r.data.user, profile_completed: true });
      toast.success("Details saved", { description: "Your dealer profile is up to date." });
      navigate("/dealer-portal");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally { setBusy(false); }
  };

  return (
    <div data-testid="dealer-profile-page">
      <PageHeader
        kicker={firstRun ? "One Quick Step" : "Dealer Profile"}
        title={firstRun ? "Confirm your details" : "Update your details"}
        sub="Keep your contact and shipping info current so orders and dealer assets reach the right place."
        image="/assets/img/w-gallery-img-5882.jpg"
      />
      <section className="mx-auto max-w-3xl px-4 py-16 sm:px-6 lg:py-24">
        {checking || !form ? (
          <p className="font-mono text-sm uppercase tracking-widest text-slate-500">Loading your profile...</p>
        ) : (
          <Reveal>
            <form onSubmit={submit} className="clip-corner border border-girder bg-obsidian p-6 sm:p-8" data-testid="dealer-profile-form">
              <p className="flex items-center gap-2 font-mono text-xs uppercase tracking-[0.25em] text-hivis"><ClipboardCheck className="h-3.5 w-3.5" /> Account Details</p>
              <div className="mt-5 grid gap-4 sm:grid-cols-2">
                <div><label className={labelCls} htmlFor="dp-name">Contact name *</label><input id="dp-name" required value={form.name} onChange={set("name")} className={inputCls} data-testid="dp-name" /></div>
                <div><label className={labelCls} htmlFor="dp-company">Company *</label><input id="dp-company" required value={form.company} onChange={set("company")} className={inputCls} data-testid="dp-company" /></div>
                <div><label className={labelCls} htmlFor="dp-phone">Phone *</label><input id="dp-phone" required value={form.phone} onChange={set("phone")} className={inputCls} data-testid="dp-phone" /></div>
                <div><label className={labelCls} htmlFor="dp-website">Website</label><input id="dp-website" value={form.website} onChange={set("website")} className={inputCls} data-testid="dp-website" placeholder="https://" /></div>
                <div>
                  <label className={labelCls} htmlFor="dp-email">Email (your login) *</label>
                  <input id="dp-email" type="email" required value={form.email} onChange={set("email")} className={inputCls} data-testid="dp-email" />
                  {form.aliases.length > 0 && (
                    <p className="mt-2 text-xs leading-relaxed text-slate-500" data-testid="dp-aliases">
                      These emails also log into this account: {form.aliases.join(", ")}
                    </p>
                  )}
                </div>
                <div>
                  <label className={labelCls} htmlFor="dp-preferred">Preferred contact method</label>
                  <select id="dp-preferred" value={form.preferred_contact} onChange={set("preferred_contact")} className={inputCls} data-testid="dp-preferred">
                    <option>Email</option><option>Phone call</option><option>Text message</option>
                  </select>
                </div>
              </div>
              <div className="mt-4"><label className={labelCls} htmlFor="dp-address">Shipping address</label><textarea id="dp-address" rows={3} value={form.shipping_address} onChange={set("shipping_address")} className={inputCls} data-testid="dp-address" placeholder="Street, city, state, ZIP" /></div>
              <div className="mt-4 space-y-3 border border-girder bg-steel/30 p-4" data-testid="dp-directory-options">
                <p className={labelCls}>Public dealer directory</p>
                <label className="flex cursor-pointer items-start gap-3 text-sm text-slate-300">
                  <input type="checkbox" checked={form.directory_opt_in} onChange={(e) => setForm({ ...form, directory_opt_in: e.target.checked })} className="mt-0.5 h-4 w-4 accent-amber-500" data-testid="dp-directory-opt-in" />
                  <span>List my business in the public dealer directory (uses the address, phone and website above)</span>
                </label>
                {form.directory_opt_in && !form.website.trim() && (
                  <p className="border border-hivis/40 bg-hivis/5 px-3 py-2 text-xs leading-relaxed text-hivis" data-testid="dp-website-required-note">
                    Your business won't appear on the Dealer Locator until you add your website above — listings without a website stay hidden.
                  </p>
                )}
                <label className="flex cursor-pointer items-start gap-3 text-sm text-slate-300">
                  <input type="checkbox" checked={form.directory_show_email} onChange={(e) => setForm({ ...form, directory_show_email: e.target.checked })} className="mt-0.5 h-4 w-4 accent-amber-500" data-testid="dp-directory-show-email" />
                  <span>Also show my email address publicly on my listing</span>
                </label>
              </div>
              <div className="mt-4"><label className={labelCls} htmlFor="dp-notes">Anything we should know?</label><textarea id="dp-notes" rows={3} value={form.notes} onChange={set("notes")} className={inputCls} data-testid="dp-notes" placeholder="Delivery notes, secondary contacts, purchasing preferences..." /></div>
              <button type="submit" disabled={busy} data-testid="dealer-profile-submit" className="clip-corner mt-6 flex w-full items-center justify-center gap-3 bg-hivis py-3.5 font-display text-lg font-bold uppercase tracking-wide text-obsidian transition-colors hover:bg-hivis-hover disabled:opacity-60">
                {busy ? "Saving..." : "Save & Continue"} <Save className="h-4 w-4" />
              </button>
            </form>
          </Reveal>
        )}
      </section>
    </div>
  );
}
